import React, { useState, useEffect } from 'react';
import { fetchComments, createComment } from '../../services/blogService';

const CommentsSection = ({ postId }) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState({ user_name: '', user_email: '', content: '' });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!postId) return;
        loadComments();
    }, [postId]);

    const loadComments = async () => {
        try {
            setLoading(true);
            const data = await fetchComments(postId);
            setComments(data || []);
        } catch (e) {
            console.error("Failed to load comments", e);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.user_name || !newComment.content) return;

        try {
            setSubmitting(true);
            setError(null);
            await createComment({ ...newComment, post_id: Number(postId) });
            setSuccess(true);
            setNewComment({ user_name: '', user_email: '', content: '' });
            // Don't reload immediately as it requires approval
        } catch (e) {
            setError(e.message || "Failed to post comment");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-12 max-w-3xl mx-auto border-t pt-8">
            <h3 className="text-2xl font-light mb-8 text-gray-900">Discussion ({comments.length})</h3>

            {/* List */}
            <div className="space-y-8 mb-12">
                {loading ? (
                    <p className="text-gray-500">Loading comments...</p>
                ) : comments.length === 0 ? (
                    <p className="text-gray-500 italic">No comments yet. Be the first to share your thoughts!</p>
                ) : (
                    comments.map((c) => (
                        <div key={c.id} className="flex gap-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg">
                                {c.user_name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">{c.user_name}</span>
                                    <span className="text-sm text-gray-400">• {new Date(c.created_at).toLocaleDateString()}</span>
                                </div>
                                <p className="text-gray-600 leading-relaxed">{c.content}</p>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Form */}
            <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="text-lg font-medium mb-4 text-gray-900">Leave a Reply</h4>
                {success ? (
                    <div className="p-4 bg-green-50 text-green-700 rounded border border-green-200">
                        Thank you! Your comment has been submitted and is awaiting moderation.
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    value={newComment.user_name}
                                    onChange={(e) => setNewComment({ ...newComment, user_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email (kept private) *</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    value={newComment.user_email}
                                    onChange={(e) => setNewComment({ ...newComment, user_email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Comment *</label>
                            <textarea
                                required
                                rows="4"
                                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                value={newComment.content}
                                onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                            ></textarea>
                        </div>

                        {error && <p className="text-red-600 text-sm">{error}</p>}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                            {submitting ? 'Posting...' : 'Post Comment'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CommentsSection;
